export type FieldWidth = 'xs' | 'sm' | 'md' | 'lg';

/**
 * Widths for the opt-in field-width extension. Clinic forms mix short numeric
 * entries with long free text, so the sizes are deliberately coarse: staff
 * scanning a column of vitals should see one input width, not one per label.
 *
 * These are the rendered width, not a ceiling. A field-width item is sized
 * from its content (see SurveyItem's `cappedRoot`) and chroma's min-widths are
 * relaxed so a control can reach the narrower sizes, which together leave a
 * max-width alone doing nothing: the box collapses to whichever is wider, the
 * label or an empty input, so "Lot #" renders narrower than "Inserted by".
 */
export const FIELD_WIDTHS: Record<FieldWidth, string> = {
  xs: '6rem',
  sm: '12rem',
  md: '18rem',
  lg: '26rem',
};

/**
 * Does the control size itself rather than inheriting the container's cap?
 *
 * A quantity renders its unit beside the input ("168 pounds"), both inside the
 * item container. Sizing the container splits the width between them, so the
 * input ends up as wide as the declared width minus the unit text: "%" leaves
 * a wide box and "pounds" a narrow one, for the same declared width. Quantity
 * items therefore always size the input itself. That still applies when no
 * unit is visible (missing reference quantity / label): skipping the container
 * width without also sizing the input would leave the box full width.
 */
export const inputCarriesFieldWidth = (fhirType?: string): boolean =>
  fhirType === 'quantity';
